import { NavLink, useNavigate } from "react-router-dom";
import { Inbox, LogOut, Mail, Users, LayoutDashboard, ShieldCheck, FolderOpen, FilePlus2, PenSquare, Scale, MessageSquare, CreditCard, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, roleOf } from "@/lib/roles";
import { canOrder } from "@/lib/cases";

const ADMIN_NAV = [
  { to: "/admin/inbox", label: "Access requests", icon: Inbox, id: "inbox" },
  { to: "/admin/orgs", label: "Organizations", icon: ShieldCheck, id: "orgs" },
  { to: "/ops", label: "Work queue", icon: FolderOpen, id: "ops" },
  { to: "/admin/cases", label: "All cases", icon: FolderOpen, id: "admin-cases" },
  { to: "/admin/opt-ins", label: "Opt-ins", icon: Mail, id: "optins" },
  { to: "/admin/privacy", label: "Privacy requests", icon: Scale, id: "privacy" },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare, id: "messages" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, id: "admin-analytics" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, id: "payments" },
  { to: "/admin/users", label: "Users & invites", icon: Users, id: "users" },
  { to: "/admin/content", label: "Site content", icon: PenSquare, id: "content" },
];

const consoleNav = (u) => {
  if (u?.account_type === "consumer") return [{ to: "/me", label: "My requests", icon: LayoutDashboard, id: "me" }];
  if (u?.platform_role === "field_agent") return [{ to: "/field", label: "My visits", icon: LayoutDashboard, id: "field" }];
  if (["analyst", "qa_reviewer"].includes(u?.platform_role)) return [{ to: "/ops", label: "Work queue", icon: FolderOpen, id: "ops" }];
  if (!u?.org_id) return [{ to: "/console", label: "Console", icon: LayoutDashboard, id: "console" }];
  const nav = [
    { to: "/console", label: "Overview", icon: LayoutDashboard, id: "console", end: true },
    { to: "/console/cases", label: "Cases", icon: FolderOpen, id: "cases", end: true },
  ];
  if (canOrder(u)) nav.push({ to: "/console/cases/new", label: "New verification", icon: FilePlus2, id: "new-case" });
  if (["org_admin", "requester"].includes(u.org_role)) nav.push({ to: "/console/analytics", label: "Analytics", icon: BarChart3, id: "analytics" });
  if (u.org_role === "org_admin") nav.push({ to: "/console/team", label: "Team", icon: Users, id: "team" });
  return nav;
};

const NavItem = ({ item }) => (
  <NavLink
    to={item.to}
    end={item.end}
    data-testid={`sidebar-${item.id}-link`}
    className={({ isActive }) =>
      `flex shrink-0 items-center gap-3 whitespace-nowrap rounded-xl px-3 py-2.5 text-sm transition-colors ${
        isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
      }`
    }
  >
    <item.icon className="h-4 w-4" strokeWidth={1.75} />
    {item.label}
  </NavLink>
);

export const AppShell = ({ children, title, eyebrow, actions }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = user?.platform_role === "super_admin" ? ADMIN_NAV : consoleNav(user);

  const signOut = async () => {
    await logout();
    navigate("/signin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-oath-bg lg:flex">
      <aside className="flex flex-col bg-oath-navy px-4 py-4 lg:sticky lg:top-0 lg:h-screen lg:w-64 lg:py-6" data-testid="app-sidebar">
        <div className="flex items-center gap-2.5 px-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-white text-oath-navy"><ShieldCheck className="h-4 w-4" /></span>
          <div>
            <div className="font-display text-sm font-bold text-white">ResidenceProof</div>
            <div className="font-mono-label text-[9px] text-slate-500">CONSOLE</div>
          </div>
        </div>
        <nav className="-mx-4 mt-4 flex gap-1 overflow-x-auto px-4 pb-1 lg:mx-0 lg:mt-8 lg:flex-col lg:px-0">
          {nav.map((n) => <NavItem key={n.id} item={n} />)}
          <span className="w-2 shrink-0 lg:hidden" />
        </nav>
        <div className="mt-4 flex items-center justify-between gap-2 border-t border-white/10 pt-4 lg:mt-auto lg:block">
          <div className="min-w-0 px-2">
            <div className="truncate text-sm text-white" data-testid="sidebar-user-name">{user?.name || user?.email}</div>
            <div className="truncate font-mono-label text-[9px] text-slate-500" data-testid="sidebar-user-role">
              {ROLE_LABELS[roleOf(user)]}{user?.org_name ? ` · ${user.org_name}` : ""}
            </div>
          </div>
          <button onClick={signOut} data-testid="sidebar-logout-button"
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-white lg:mt-3">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 px-5 py-8 sm:px-8 lg:px-12 lg:py-10">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            {eyebrow && <div className="font-mono-label text-[11px] font-semibold text-oath-accent">{eyebrow}</div>}
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-oath-ink sm:text-4xl" data-testid="page-title">{title}</h1>
          </div>
          {actions}
        </header>
        {children}
      </main>
    </div>
  );
};

export default AppShell;
